import React from "react";
import { Stack, Text, Chip, Group, CheckIcon, rem } from "@mantine/core";
import styles from "./ChipGroup.module.css";

interface ChipOption {
  value: string;
  label: string;
}

interface ChipGroupSingleProps {
  label: string;
  options: ChipOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  required?: boolean;
}

// Chip style constants
const CHIP_STYLES = {
  lightTealBg: "rgba(54, 175, 201, 0.1)",
  primaryTeal: "#36AFC9",
  greyCheck: "#757575",
};

// Custom grey CheckIcon with thinner stroke
const GreyCheckIcon = () => (
  <CheckIcon size={rem(14)} color={CHIP_STYLES.greyCheck} stroke="1px" />
);

export default function ChipGroupSingle({
  label,
  options,
  value,
  onChange,
  required,
}: ChipGroupSingleProps) {
  const handleChange = (optionValue: string) => (checked: boolean) => {
    // Toggle off if clicking the same value (unchecking)
    if (!checked && value === optionValue) {
      onChange(null);
    } else if (checked) {
      onChange(optionValue);
    }
  };

  return (
    <Stack gap="xs" className={styles.container}>
      <Text size="md" fw={400} c="#1e1e1e">
        {label}
        {required && <span className={styles.required}>*</span>}
      </Text>
      <Group gap="xs" className={styles.chipGroup}>
        {options.map((option) => {
          const isChecked = value === option.value;
          return (
            <Chip
              key={option.value}
              checked={isChecked}
              onChange={handleChange(option.value)}
              size="md"
              variant="outline"
              color="teal"
              icon={<GreyCheckIcon />}
              classNames={{
                label: styles.chipLabel,
              }}
              styles={{
                label: {
                  backgroundColor: isChecked
                    ? CHIP_STYLES.lightTealBg
                    : undefined,
                  borderColor: isChecked ? CHIP_STYLES.primaryTeal : undefined,
                  borderWidth: isChecked ? 2 : 1,
                  // Keep text color black (default) even when selected
                  color: "#101820",
                  fontSize: rem(16), // Match input label font size (1rem = 16px)
                },
              }}
            >
              {option.label}
            </Chip>
          );
        })}
      </Group>
    </Stack>
  );
}
